import * as z from 'zod';

const envSchema = z.object({
  NODE_ENV: z.string().nonempty(),
  NEXT_PUBLIC_ENVIRONMENT: z.string().nonempty(),
  NEXT_PUBLIC_BUILD_COMMIT_SHA: z.string(),
  NEXT_PUBLIC_URL: z.string().url(),
  NEXT_PUBLIC_API_URL: z.string().url(),
  NEXT_PUBLIC_BASE_API_URL: z.string().url(),
  NEXT_PUBLIC_REQUEST_API_TIMEOUT_DEFAULT: z.coerce.number().int().positive(),
  NEXT_PUBLIC_SUPPLIER_BASE_URL: z.string().url(),
  NEXT_PUBLIC_SUPERTOKENS_API_DOMAIN: z.string().url(),
  NEXT_PUBLIC_SUPERTOKENS_WEBSITE_DOMAIN: z.string().url(),
  NEXT_PUBLIC_FIREBASE_API_KEY: z.string().nonempty(),
  NEXT_PUBLIC_FIREBASE_PROJECT_ID: z.string().nonempty(),
  NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: z.string().nonempty(),
  NEXT_PUBLIC_FIREBASE_APP_ID: z.string().nonempty(),
  NEXT_PUBLIC_GOOGLE_ANALYTICS_ID: z.string().nonempty(),
  NEXT_PUBLIC_DD_RUM_APPLICATION_ID: z.string(),
  NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN: z.string(),
});

const nextPublicSchema = z.object({
  ENVIRONMENT: z.string(),
  BUILD_COMMIT_SHA: z.string(),
  URL: z.string().url(),
  API_URL: z.string().url(),
  BASE_API_URL: z.string().url(),
  REQUEST_API_TIMEOUT_DEFAULT: z.number().int().positive(),
  SUPPLIER_BASE_URL: z.string().url(),
  SUPERTOKENS_API_DOMAIN: z.string().url(),
  SUPERTOKENS_WEBSITE_DOMAIN: z.string().url(),
  GOOGLE_ANALYTICS_ID: z.string(),
  DD_RUM_APPLICATION_ID: z.string().optional(),
  DD_RUM_CLIENT_TOKEN: z.string().optional(),
  NODE_ENV: z.string(),
  firebase: z.object({
    apiKey: z.string(),
    authDomain: z.string(),
    projectId: z.string(),
    storageBucket: z.string(),
    messagingSenderId: z.string(),
    appId: z.string(),
  }),
});

function emptyToUndefined(value: string | undefined): string | undefined {
  if (value === undefined || value === '') {
    return undefined;
  }
  return value;
}

function validateNextPublic() {
  const envResult = envSchema.safeParse({
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT,
    NEXT_PUBLIC_BUILD_COMMIT_SHA:
      process.env.NEXT_PUBLIC_BUILD_COMMIT_SHA ?? '',
    NEXT_PUBLIC_URL: process.env.NEXT_PUBLIC_URL,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_BASE_API_URL: process.env.NEXT_PUBLIC_BASE_API_URL,
    NEXT_PUBLIC_REQUEST_API_TIMEOUT_DEFAULT:
      process.env.NEXT_PUBLIC_REQUEST_API_TIMEOUT_DEFAULT,
    NEXT_PUBLIC_SUPPLIER_BASE_URL: process.env.NEXT_PUBLIC_SUPPLIER_BASE_URL,
    NEXT_PUBLIC_SUPERTOKENS_API_DOMAIN:
      process.env.NEXT_PUBLIC_SUPERTOKENS_API_DOMAIN,
    NEXT_PUBLIC_SUPERTOKENS_WEBSITE_DOMAIN:
      process.env.NEXT_PUBLIC_SUPERTOKENS_WEBSITE_DOMAIN,
    NEXT_PUBLIC_FIREBASE_API_KEY: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    NEXT_PUBLIC_FIREBASE_PROJECT_ID:
      process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID:
      process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    NEXT_PUBLIC_FIREBASE_APP_ID: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    NEXT_PUBLIC_GOOGLE_ANALYTICS_ID:
      process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID,
    NEXT_PUBLIC_DD_RUM_APPLICATION_ID:
      process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID ?? '',
    NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN:
      process.env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN ?? '',
  });

  if (!envResult.success) {
    const issues = envResult.error.issues.map(
      ({ path, message }) => `${path.toString()}: ${message}`
    );

    throw new Error(`Detected invalid environment:\n  ${issues.join('\n  ')}`);
  }
  const env = envResult.data;

  const envValues = {
    ENVIRONMENT: env.NEXT_PUBLIC_ENVIRONMENT,
    BUILD_COMMIT_SHA: env.NEXT_PUBLIC_BUILD_COMMIT_SHA,
    URL: env.NEXT_PUBLIC_URL,
    API_URL: env.NEXT_PUBLIC_API_URL,
    BASE_API_URL: env.NEXT_PUBLIC_BASE_API_URL,
    REQUEST_API_TIMEOUT_DEFAULT: env.NEXT_PUBLIC_REQUEST_API_TIMEOUT_DEFAULT,
    SUPPLIER_BASE_URL: env.NEXT_PUBLIC_SUPPLIER_BASE_URL,
    SUPERTOKENS_API_DOMAIN: env.NEXT_PUBLIC_SUPERTOKENS_API_DOMAIN,
    SUPERTOKENS_WEBSITE_DOMAIN: env.NEXT_PUBLIC_SUPERTOKENS_WEBSITE_DOMAIN,
    GOOGLE_ANALYTICS_ID: env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID,
    DD_RUM_APPLICATION_ID: emptyToUndefined(
      env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID
    ),
    DD_RUM_CLIENT_TOKEN: emptyToUndefined(env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN),
    NODE_ENV: env.NODE_ENV,
    firebase: {
      apiKey: env.NEXT_PUBLIC_FIREBASE_API_KEY,
      authDomain: `${env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}.firebaseapp.com`,
      projectId: env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
      storageBucket: `${env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}.firebasestorage.app`,
      messagingSenderId: env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
      appId: env.NEXT_PUBLIC_FIREBASE_APP_ID,
    },
  };

  const result = nextPublicSchema.safeParse(envValues);

  if (result.success) {
    return result.data;
  }

  const issues = result.error.issues.map(
    ({ path, message }) => `${path.toString()}: ${message}`
  );

  throw new Error(`Detected invalid environment:\n  ${issues.join('\n  ')}`);
}

const ENV = validateNextPublic();

export { ENV };
